/* eslint-disable @typescript-eslint/indent */

import { ReactNode } from 'react';
import clsx from 'clsx';

import { Link } from '@northladder/i18n';

import { ButtonLoader } from './ButtonLoader';

type TButtonType = 'button' | 'navLink';

interface IBaseAppButtonOptions {
  className?: string;
  buttonType?: TButtonType;
  iconEnd?: ReactNode;
  iconStart?: ReactNode;
  isEnabled?: boolean;
  isLoading?: boolean;
  isStartIconEnabled?: boolean;
  size?: 'default' | 'small';
  title: string | ReactNode;
  titleShort?: string;
  type?: 'outlined' | 'solid';
  variants?: 'primary' | 'danger' | 'default';
}

interface IAppButton extends IBaseAppButtonOptions {
  buttonType?: 'button';
  onClick: () => void;
}

interface INavLinkAppButton extends IBaseAppButtonOptions {
  buttonType: 'navLink';
  /**
   * Allows to use a link instead of a button.
   */
  href: string;
}

export type TAppButtonOptions = IAppButton | INavLinkAppButton;

export interface IMainCTAOptions
  extends IBaseAppButtonOptions,
    IBaseAppButtonOptions {
  label: string;
  shortLabel?: string;
}

/**
 * -----------------------------------------------------------------------------
 * It renders a button with a text and plus icon, and allows the user to either
 * pass a callback function or a link based on the `buttonType`.
 */
export function AppButton(props: Readonly<TAppButtonOptions>) {
  const {
    className,
    iconEnd,
    iconStart,
    isEnabled = true,
    isLoading,
    isStartIconEnabled = true,
    size = 'default',
    title,
    titleShort,
    type = 'solid',
    variants = 'primary',
  } = props;

  let iconSizeClassName = 'w-4 md:w-6 transition-all duration-500 ease-in-out';
  let textSizeClassName =
    'text-xs md:text-sm transition-all duration-500 ease-in-out';
  let colorClassName = '';

  if (size === 'small') {
    iconSizeClassName = 'w-3 md:w-4';
    textSizeClassName = 'text-xs';
  }

  if (type === 'solid' && variants === 'primary') {
    colorClassName = 'border-green-400 bg-green-600 text-white';
  } else if (type === 'solid' && variants === 'danger') {
    colorClassName = 'border-red-400 bg-red-600 text-white';
  } else if (type === 'outlined' && variants === 'primary') {
    colorClassName = 'border-green-700 text-green-700 bg-white';
  } else if (type === 'outlined' && variants === 'danger') {
    colorClassName = 'border-red-400 text-red-600 bg-white';
  } else if (type === 'outlined' && variants === 'default') {
    colorClassName = `
    border-gray-300 text-gray-800 bg-white hover:bg-green-50
    hover:text-green-800 hover:border-green-900/20
    `;
  } else if (type === 'solid' && variants === 'default') {
    colorClassName = 'border-gray-300 bg-gray-800 text-white';
  }

  const btnContainerClassName = clsx(
    `
      px-2 md:px-4 py-1.5 flex items-center gap-0.5 md:gap-1
      whitespace-nowrap rounded-full border transition-all duration-1000 ease-in-out
      hover:opacity-90 hover:scale-105
    `,
    isEnabled
      ? 'hover:opacity-90 opacity-100'
      : 'opacity-40 pointer-events-none aria-disabled',
    colorClassName,
    className
  );

  if (props?.buttonType === 'navLink') {
    const { href } = props;

    return (
      <Link
        className={btnContainerClassName}
        href={href}
        tabIndex={isEnabled ? 0 : -1}
      >
        {isLoading ? (
          <ButtonLoader className="mx-4" isEnabled={isEnabled} />
        ) : null}

        {iconStart && isStartIconEnabled && !isLoading ? (
          <span className={iconSizeClassName}>{iconStart}</span>
        ) : null}

        <span className={clsx('hidden md:block', textSizeClassName)}>
          {title}
        </span>
        <span className={clsx('block md:hidden', textSizeClassName)}>
          {titleShort || title}
        </span>

        {iconEnd ? <span className={iconSizeClassName}>{iconEnd}</span> : null}
      </Link>
    );
  }

  const { onClick } = props;

  return (
    <button
      className={btnContainerClassName}
      disabled={!isEnabled}
      onClick={onClick}
      tabIndex={isEnabled ? 0 : -1}
      type="button"
    >
      {isLoading ? (
        <ButtonLoader className="me-2 h-4 w-4" isEnabled={isEnabled} />
      ) : null}

      {iconStart && isStartIconEnabled && !isLoading ? (
        <span className={iconSizeClassName}>{iconStart}</span>
      ) : null}

      <span className={clsx('hidden md:block', textSizeClassName)}>
        {title}
      </span>

      <span className={clsx('block md:hidden', textSizeClassName)}>
        {titleShort || title}
      </span>

      {iconEnd ? <span className={iconSizeClassName}>{iconEnd}</span> : null}
    </button>
  );
}
